/**
 * 根路由入口
 * 运行时根据 UA 和窗口宽度判断平台，懒加载对应路由
 * @module router
 */
import { MiniRoute, PatternBrowserRouter } from '@yimoka/mini-router';
import { ConfigProvider } from '@yimoka/react';
import { lazy, Suspense, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { PageLoading } from '@/components/ui/page-loading';
import { i18n } from '@/locales/i18n';
import { getPlatform, PLATFORM_H5, setLanguage, SUPPORT_LANGUAGES } from '@/root';

import { pathnameHasLanguagePrefix, resolvePathLanguage } from './lang';

const LazyH5Router = lazy(() => import('./h5').then(m => ({ default: m.H5Router })));
const LazyPcRouter = lazy(() => import('./pc').then(m => ({ default: m.PcRouter })));

/** 根路由组件，根据平台懒加载 H5 或 PC 端路由 */
const PageRouter = () => {
  const platform = getPlatform();
  const Router = platform === PLATFORM_H5 ? LazyH5Router : LazyPcRouter;
  return (
    <ConfigProvider value={{ useNavigate, useRouteParams: useParams, useLocation }}>
      <Suspense fallback={<PageLoading />}>
        <Router />
      </Suspense>
    </ConfigProvider>
  );
};

/**
 * URL 带语言前缀时同步到 rootStore；无前缀路径保留 getLanguage() 已有偏好。
 *
 * basename 规则与 ka/aspnet 根路由（LangRouter + hasLangPrefix）一致：
 * - URL 带 `/zh/`、`/en/` 时启用 `base="/"`，由 PatternBrowserRouter 抽出语言段作为 React Router 的 basename；
 * - 无前缀的老路径（如 `/partners/home`）不启用 base，避免把 `partners` 误当成 basename 导致无法匹配路由。
 */
const LangRouter = ({ lang }: { lang?: string }) => {
  useEffect(() => {
    const pathLang = resolvePathLanguage(lang);
    if (!pathLang) return;

    setLanguage(pathLang);
    if (i18n.language !== pathLang) {
      void i18n.changeLanguage(pathLang);
    }
  }, [lang]);

  const hasLangPrefix = pathnameHasLanguagePrefix();

  return (
    <PatternBrowserRouter key={lang} base={(lang || hasLangPrefix) ? '/' : undefined}>
      <PageRouter />
    </PatternBrowserRouter>
  );
};

export const RootRouter = () => (
  <>
    {SUPPORT_LANGUAGES.map(item => (
      <MiniRoute key={item.path} component={<LangRouter lang={item.value} />} path={`/${item.path}/*`} />
    ))}
    <MiniRoute component={<LangRouter />} path="*" />
  </>
);
